import { classnames } from '@aibees/shared/utils';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { FaqItem } from '../faq-item';
import { FAQ_ALL_TAB_ID, FAQ_SECTIONS } from './config';
import * as SX from './sx';
import type { FaqProps as Props } from './types';

export const Faq = memo(({ className }: Props) => {
  const [activeTab, setActiveTab] = useState<string>(FAQ_ALL_TAB_ID);

  const visibleSections = useMemo(() => {
    if (activeTab === FAQ_ALL_TAB_ID) return FAQ_SECTIONS;
    return FAQ_SECTIONS.filter((s) => s.id === activeTab);
  }, [activeTab]);

  return (
    <Box className={classnames('Faq', className)} sx={SX.root}>
      <Box sx={SX.headerBlock}>
        <Typography variant="h5" fontWeight={700} color="grey.700">
          Frequently Asked Questions
        </Typography>
        <Typography variant="body4" fontWeight={500} color="grey.500">
          Clear answers to questions you might have before getting started.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="fullWidth"
      >
        <Tab value={FAQ_ALL_TAB_ID} label="All" />
        {FAQ_SECTIONS.map((section) => (
          <Tab key={section.id} value={section.id} label={section.title} />
        ))}
      </Tabs>

      <Stack sx={SX.sections}>
        {visibleSections.map((section) => (
          <Box key={section.id} sx={SX.sectionGroup}>
            <Box sx={SX.sectionTitle}>
              <Typography variant="body4" fontWeight={700} color="grey.700">
                {section.title}
              </Typography>
            </Box>
            <Stack sx={SX.itemList}>
              {section.items.map((item) => (
                <FaqItem key={item.id} item={item} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
});

Faq.displayName = 'Faq';
